/**
 * SparkLayer B2B header bag badge (FXT-235).
 *
 * Horizon cart-icon / renderCartBubble read Shopify /cart.js, which stays
 * 0 in a Spark session. a564119 still failed on yellow-W: it called
 * renderCartBubble first (no-op / async when refs are missing or Spark
 * swapped the bag node), only painted .cart-bubble inside cart-icon,
 * required isSparkSession before getCart, retried getCart for 400ms,
 * and treated onReady payloads as carts (paint 0). Mini-cart can show
 * lines while the header bubble stays visually-hidden.
 *
 * Yellow-W dump (a564119 live): getCart = 7, --has-cart on, count
 * text already 7. renderCartBubble only changes the number; computed
 * style stays display:none / 0×0 (Spark [data-spark=b2c-only] hide
 * and/or leftover visually-hidden). Live-region SR copy stays 0.
 *
 * This pass: force D2C-visible bubble CSS+inline; strip b2c-only on
 * the bag node only; keep getCart / onCartUpdate / view-update / poll.
 *
 * Does not dispatch theme cart:update. D2C: no-op unless Spark qty > 0
 * or this is a B2B / Spark-host session.
 */
(() => {
  const HOST_SEL = [
    'spark-pdp',
    'spark-product-card',
    'spark-cart',
    'spark-mini-cart',
    'spark-my-cart',
    'spark-cart-drawer',
    'spark-drawer',
    'spark-login',
    'spark-account',
  ].join(',');
  const SCRIPT_SEL = 'script[src*="fixatech-spark-cart-count"]';
  const ATC_SEL = [
    '.fixatech-spark-pdp-atc',
    '.fixatech-spark-card-atc',
    '.fixatech-spark-atc',
    '[class*="add-to-cart"]',
    '[class*="add-to-order"]',
    '[data-testid*="add-to-cart"]',
  ].join(',');
  const SOURCE = 'fixatech-spark-cart-count';
  const LINE_KEYS = [
    'items',
    'cartItems',
    'cart_items',
    'products',
    'lineItems',
    'line_items',
    'lines',
    'orderItems',
    'order_items',
    'contents',
  ];
  const POLL_MS = 300;
  const POLL_MAX_MS = 3000;
  const STYLE_ID = 'fixatech-spark-bag-bubble';
  const BAG_ON = 'fixatech-bag-on';
  const BAG_CSS =
    '#header-component cart-icon.header-actions__cart-icon--has-cart .cart-bubble,' +
    '#header-component cart-icon.header-actions__cart-icon--has-cart .cart-bubble.visually-hidden,' +
    '#header-component cart-icon.header-actions__cart-icon--has-cart .cart-bubble.hidden,' +
    '#header-component cart-icon.header-actions__cart-icon--has-cart .cart-bubble[hidden],' +
    '#header-component cart-icon.header-actions__cart-icon--has-cart .cart-bubble[data-spark="b2c-only"],' +
    '#header-component .header-actions__cart-icon--has-cart .cart-bubble.fixatech-bag-on,' +
    '#header-component .header-actions__cart-icon--has-cart .cart-bubble.fixatech-bag-on[data-spark="b2c-only"]{' +
    'display:flex!important;visibility:visible!important;opacity:1!important;' +
    'position:absolute!important;width:20px!important;height:20px!important;' +
    'min-width:20px!important;min-height:20px!important;overflow:visible!important;' +
    'clip:auto!important;clip-path:none!important;margin:0!important;padding:0!important;' +
    'inset:auto!important;top:4.5px!important;right:2.5px!important;left:auto!important;' +
    'bottom:auto!important;z-index:6!important;pointer-events:none;aspect-ratio:1;}' +
    '#header-component cart-icon.header-actions__cart-icon--has-cart .cart-bubble__text-count,' +
    '#header-component cart-icon.header-actions__cart-icon--has-cart .cart-bubble__text-count.hidden{' +
    'display:flex!important;visibility:visible!important;}';

  const installBagStyle = () => {
    let el = document.getElementById(STYLE_ID);
    if (!el) {
      el = document.createElement('style');
      el.id = STYLE_ID;
      el.textContent = BAG_CSS;
    }
    (document.head || document.documentElement).appendChild(el);
  };

  const isLiquidB2b = () => {
    const el = document.querySelector(SCRIPT_SEL);
    return Boolean(el && el.hasAttribute('data-fixatech-b2b'));
  };

  const hasSparkHost = () => Boolean(document.querySelector(HOST_SEL));

  const isSparkSession = () => isLiquidB2b() || hasSparkHost();

  const toQty = (value) => {
    const n = Number(value);
    return Number.isFinite(n) && n > 0 ? n : 0;
  };

  const lineQty = (line) => {
    if (line == null) return 0;
    if (typeof line !== 'object') return toQty(line);
    const raw =
      line.quantity ??
      line.qty ??
      line.resultantQuantity ??
      line.resultant_quantity ??
      line.requestedQuantity ??
      line.quantityOrdered;
    if (raw && typeof raw === 'object') {
      return toQty(raw.amount ?? raw.value ?? raw.qty ?? raw.quantity);
    }
    return toQty(raw);
  };

  const looksLikeLine = (value) => {
    if (!value || typeof value !== 'object') return false;
    return (
      value.quantity != null ||
      value.qty != null ||
      value.resultantQuantity != null ||
      value.sku != null ||
      value.itemKey != null ||
      value.merchandise != null
    );
  };

  const asLines = (rows) => {
    if (Array.isArray(rows)) return rows;
    if (!rows || typeof rows !== 'object') return null;
    if (Array.isArray(rows.edges)) {
      return rows.edges.map((edge) => (edge && (edge.node || edge.item)) || edge);
    }
    if (Array.isArray(rows.nodes)) return rows.nodes;
    if (Array.isArray(rows.items)) return rows.items;
    const values = Object.keys(rows).map((key) => rows[key]);
    if (values.length && values.every(looksLikeLine)) return values;
    return null;
  };

  const linesFrom = (cart) => {
    if (!cart || typeof cart !== 'object' || Array.isArray(cart)) return null;
    for (let i = 0; i < LINE_KEYS.length; i += 1) {
      const lines = asLines(cart[LINE_KEYS[i]]);
      if (lines) return lines;
    }
    if (cart.cart && cart.cart !== cart) return linesFrom(cart.cart);
    if (cart.data && cart.data !== cart) return linesFrom(cart.data);
    return null;
  };

  const totalFrom = (cart) => {
    if (!cart || typeof cart !== 'object') return null;
    const totals = cart.totals && typeof cart.totals === 'object' ? cart.totals : {};
    const raw =
      cart.itemCount ??
      cart.item_count ??
      cart.totalQuantity ??
      cart.total_quantity ??
      cart.quantity ??
      totals.itemCount ??
      totals.quantity ??
      totals.itemQuantity;
    if (raw == null) return null;
    if (raw && typeof raw === 'object') {
      return toQty(raw.amount ?? raw.value ?? raw.qty ?? raw.quantity);
    }
    const n = Number(raw);
    return Number.isFinite(n) && n >= 0 ? n : null;
  };

  const parseCount = (cart) => {
    if (cart == null) return null;
    if (typeof cart === 'number' && Number.isFinite(cart) && cart >= 0) return { count: cart };
    if (typeof cart === 'string') {
      try {
        return parseCount(JSON.parse(cart));
      } catch (err) {
        return null;
      }
    }
    if (typeof cart !== 'object') return null;
    if (Array.isArray(cart)) {
      const sum = cart.reduce((acc, line) => acc + lineQty(line), 0);
      return cart.length ? { count: sum } : null;
    }
    const lines = linesFrom(cart);
    if (lines) {
      const sum = lines.reduce((acc, line) => acc + lineQty(line), 0);
      const total = totalFrom(cart);
      return { count: total != null && total > sum ? total : sum };
    }
    const total = totalFrom(cart);
    if (total != null) return { count: total };
    if (cart.cart && cart.cart !== cart) return parseCount(cart.cart);
    if (cart.data && cart.data !== cart) return parseCount(cart.data);
    return null;
  };

  const rememberCount = (count) => {
    try {
      sessionStorage.setItem(
        'cart-count',
        JSON.stringify({
          value: count < 100 ? String(count) : '',
          timestamp: Date.now(),
        })
      );
    } catch (err) {}
  };

  const scopes = () => {
    const roots = [document];
    document.querySelectorAll('#header-component, header-component, header-actions, cart-icon').forEach((el) => {
      roots.push(el);
      if (el.shadowRoot) roots.push(el.shadowRoot);
    });
    return roots;
  };

  const queryAll = (selector) => {
    const found = [];
    const seen = typeof Set === 'function' ? new Set() : null;
    scopes().forEach((root) => {
      if (!root.querySelectorAll) return;
      try {
        root.querySelectorAll(selector).forEach((el) => {
          if (seen) {
            if (seen.has(el)) return;
            seen.add(el);
          }
          found.push(el);
        });
      } catch (err) {}
    });
    return found;
  };

  const bagHosts = () => {
    const hosts = queryAll(
      '#header-component cart-icon, #header-component .header-actions__cart-icon, #header-component [data-testid="cart-icon"], header-actions cart-icon, header-actions .header-actions__cart-icon, cart-icon, .header-actions__cart-icon'
    );
    return hosts.filter((el) => {
      if (!el || !el.closest) return true;
      return !el.closest('.cart-drawer__heading, cart-drawer-component dialog, [data-testid="cart-drawer-dialog"]');
    });
  };

  const ensureBubble = (icon) => {
    let bubble = icon.querySelector('.cart-bubble');
    if (bubble) return bubble;
    bubble = document.createElement('div');
    bubble.className = 'cart-bubble visually-hidden';
    bubble.setAttribute('data-maintain-ratio', '');
    bubble.setAttribute('data-fixatech-spark-bubble', '');
    bubble.innerHTML =
      '<span class="cart-bubble__background"></span>' +
      '<span class="cart-bubble__text">' +
      '<span class="cart-bubble__text-count hidden" data-testid="cart-bubble" aria-hidden="true"></span>' +
      '</span>';
    icon.appendChild(bubble);
    return bubble;
  };

  const countLabel = (bubble) => {
    const sr = bubble && bubble.querySelector('.cart-bubble__text > .visually-hidden');
    if (sr && sr.textContent) {
      const prefix = sr.textContent.split(':')[0];
      if (prefix && prefix.trim()) return prefix.trim();
    }
    if (window.Theme && Theme.translations && Theme.translations.cart_count) {
      return Theme.translations.cart_count;
    }
    return '';
  };

  const paintCopy = (bubble, count) => {
    const label = countLabel(bubble);
    const text = label ? `${label}: ${count}` : String(count);
    const sr = bubble && bubble.querySelector('.cart-bubble__text > .visually-hidden');
    if (sr) sr.textContent = text;
    queryAll('[data-testid="cart-count-live-region"]').forEach((live) => {
      live.textContent = text;
    });
  };

  const revealBubble = (bubble, countEl, count) => {
    bubble.classList.remove('visually-hidden', 'hidden');
    bubble.classList.add(BAG_ON);
    bubble.removeAttribute('hidden');
    bubble.hidden = false;
    if (bubble.getAttribute('data-spark') === 'b2c-only') {
      bubble.removeAttribute('data-spark');
    }
    countEl.classList.remove('hidden', 'visually-hidden');
    countEl.removeAttribute('hidden');
    countEl.textContent = count > 0 && count < 100 ? String(count) : '';
    const box = {
      display: 'flex',
      visibility: 'visible',
      opacity: '1',
      position: 'absolute',
      width: '20px',
      height: '20px',
      'min-width': '20px',
      'min-height': '20px',
      overflow: 'visible',
      clip: 'auto',
      'clip-path': 'none',
      margin: '0',
      padding: '0',
      inset: 'auto',
      top: '4.5px',
      right: '2.5px',
      left: 'auto',
      bottom: 'auto',
      'z-index': '6',
    };
    Object.keys(box).forEach((prop) => bubble.style.setProperty(prop, box[prop], 'important'));
    const bg = bubble.querySelector('.cart-bubble__background');
    if (bg) {
      bg.style.setProperty('display', 'block', 'important');
      bg.style.setProperty('background-color', 'var(--fixatech-primary, #ffc600)', 'important');
      bg.style.setProperty('border-radius', '50%', 'important');
    }
    const text = bubble.querySelector('.cart-bubble__text');
    if (text) {
      text.style.setProperty('display', 'flex', 'important');
      text.style.setProperty('color', 'var(--fixatech-background-dark, #141414)', 'important');
      text.style.setProperty('font-size', '10px', 'important');
      text.style.setProperty('font-weight', '700', 'important');
    }
    paintCopy(bubble, count);
  };

  const hideBubble = (bubble, countEl) => {
    bubble.classList.remove(BAG_ON);
    bubble.classList.add('visually-hidden');
    countEl.classList.add('hidden');
    countEl.textContent = '';
    bubble.removeAttribute('style');
    const bg = bubble.querySelector('.cart-bubble__background');
    const text = bubble.querySelector('.cart-bubble__text');
    if (bg) bg.removeAttribute('style');
    if (text) text.removeAttribute('style');
    paintCopy(bubble, 0);
  };

  const paintDom = (icon, count) => {
    const bubble = ensureBubble(icon);
    const countEl = bubble.querySelector('.cart-bubble__text-count') || icon.querySelector('.cart-bubble__text-count');
    if (!countEl) return;
    if (count > 0) {
      icon.classList.add('header-actions__cart-icon--has-cart');
      revealBubble(bubble, countEl, count);
    } else {
      icon.classList.remove('header-actions__cart-icon--has-cart');
      hideBubble(bubble, countEl);
    }
  };

  const paintIcon = (icon, count) => {
    paintDom(icon, count);
  };

  const isBubbleShown = (bubble) => {
    if (!bubble) return false;
    try {
      const style = window.getComputedStyle(bubble);
      if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') return false;
      const box = bubble.getBoundingClientRect();
      return box.width >= 8 && box.height >= 8;
    } catch (err) {
      return !bubble.classList.contains('visually-hidden');
    }
  };

  const visibleCount = () => {
    const hosts = bagHosts();
    for (let i = 0; i < hosts.length; i += 1) {
      const bubble = hosts[i].querySelector('.cart-bubble');
      const countEl = hosts[i].querySelector('.cart-bubble__text-count');
      if (!bubble || !countEl) continue;
      if (!isBubbleShown(bubble)) return 0;
      const n = parseInt(countEl.textContent, 10);
      return Number.isFinite(n) ? n : 0;
    }
    return -1;
  };

  let lastCount = -1;

  const paintCount = (count, force) => {
    const n = Math.max(0, Math.floor(Number(count) || 0));
    const shown = n > 0 && visibleCount() === n;
    if (!force && n === lastCount && shown) return;
    lastCount = n;

    const run = () => {
      installBagStyle();
      const hosts = bagHosts();
      if (hosts.length) {
        hosts.forEach((icon) => paintIcon(icon, n));
      } else {
        queryAll('.cart-bubble').forEach((bubble) => {
          const host = bubble.closest('cart-icon, .header-actions__cart-icon') || bubble.parentElement;
          if (host) paintDom(host, n);
        });
      }
      rememberCount(n);
    };

    if (window.customElements && !customElements.get('cart-icon') && customElements.whenDefined) {
      customElements.whenDefined('cart-icon').then(run).catch(run);
      return;
    }
    run();
  };

  let polling = false;

  const applyCart = (cart, allowEmpty) => {
    const parsed = parseCount(cart);
    if (parsed == null) return false;
    if (parsed.count > 0) {
      paintCount(parsed.count);
      return true;
    }
    if (polling && !allowEmpty) return false;
    if (isSparkSession()) {
      paintCount(0);
      return true;
    }
    return false;
  };

  const unwrap = (value, depth) => {
    if (depth > 4 || value == null) return Promise.resolve(value);
    if (typeof value.then === 'function') {
      return Promise.resolve(value).then((next) => unwrap(next, depth + 1));
    }
    if (typeof value === 'string') {
      try {
        return unwrap(JSON.parse(value), depth + 1);
      } catch (err) {
        return Promise.resolve(value);
      }
    }
    return Promise.resolve(value);
  };

  let reading = false;

  const readCart = () => {
    const spark = window.spark;
    if (!spark) return Promise.resolve(null);
    reading = true;
    try {
      let pending = null;
      if (typeof spark.getCart === 'function') pending = unwrap(spark.getCart(), 0);
      else if (typeof spark.getCartCache === 'function') pending = unwrap(spark.getCartCache(), 0);
      if (pending) {
        return pending.then(
          (cart) => {
            reading = false;
            return cart;
          },
          (err) => {
            reading = false;
            throw err;
          }
        );
      }
    } catch (err) {
      reading = false;
      return Promise.resolve(null);
    }
    reading = false;
    return Promise.resolve(null);
  };

  let pollTimer = 0;
  let pollUntil = 0;
  let pollFails = 0;

  const stopPoll = () => {
    polling = false;
    pollUntil = 0;
    if (pollTimer) {
      clearTimeout(pollTimer);
      pollTimer = 0;
    }
  };

  const pollTick = () => {
    pollTimer = 0;
    readCart()
      .then((cart) => {
        if (applyCart(cart)) {
          stopPoll();
          return;
        }
        if (parseCount(cart) == null) {
          pollFails += 1;
          if (pollFails > 1 || Date.now() >= pollUntil) {
            polling = false;
            if (pollTimer) {
              clearTimeout(pollTimer);
              pollTimer = 0;
            }
            return;
          }
          pollTimer = setTimeout(pollTick, POLL_MS);
          return;
        }
        if (Date.now() < pollUntil) {
          pollTimer = setTimeout(pollTick, POLL_MS);
          return;
        }
        polling = false;
        applyCart(cart, true);
      })
      .catch(() => {
        pollFails += 1;
        if (pollFails > 1 || Date.now() >= pollUntil) {
          polling = false;
          if (pollTimer) {
            clearTimeout(pollTimer);
            pollTimer = 0;
          }
          return;
        }
        pollTimer = setTimeout(pollTick, POLL_MS);
      });
  };

  const startPoll = () => {
    if (!window.spark && !isSparkSession()) return;
    polling = true;
    pollFails = 0;
    pollUntil = Date.now() + POLL_MAX_MS;
    if (pollTimer) clearTimeout(pollTimer);
    pollTick();
  };

  const syncFromSpark = () => {
    if (!window.spark) return;
    readCart()
      .then((cart) => {
        applyCart(cart);
      })
      .catch(() => {});
  };

  const onSparkCart = (event) => {
    const detail = event && event.detail ? event.detail : {};
    if (applyCart(detail.cart) || applyCart(detail.results)) {
      stopPoll();
      return;
    }
    if (reading) return;
    startPoll();
  };

  const resealHooks = () => {
    try {
      if (typeof window.__fixatechSealSparkCartHooks === 'function') {
        window.__fixatechSealSparkCartHooks(true);
      }
    } catch (err) {}
  };

  const patchSparkOptionsSetter = () => {
    const desc = Object.getOwnPropertyDescriptor(window, 'sparkOptions');
    if (!desc) return;
    if (desc.set && desc.set[SOURCE]) return;
    const prevGet = desc.get;
    const prevSet = desc.set;
    let current = window.sparkOptions;
    const set = function (value) {
      if (typeof prevSet === 'function') prevSet.call(this, value);
      else current = value && typeof value === 'object' ? value : {};
      resealHooks();
    };
    set[SOURCE] = true;
    try {
      Object.defineProperty(window, 'sparkOptions', {
        configurable: true,
        enumerable: true,
        get: prevGet || (() => current),
        set,
      });
    } catch (err) {}
  };

  const wrapUpdateCart = () => {
    const spark = window.spark;
    if (!spark || typeof spark.updateCart !== 'function') return;
    if (spark.updateCart[SOURCE]) return;
    const original = spark.updateCart.bind(spark);
    const wrapped = function () {
      const result = original.apply(this, arguments);
      Promise.resolve(result)
        .then((results) => {
          applyCart(results);
          startPoll();
        })
        .catch(startPoll);
      return result;
    };
    wrapped[SOURCE] = true;
    try {
      spark.updateCart = wrapped;
    } catch (err) {}
  };

  const revealExistingCount = () => {
    bagHosts().forEach((icon) => {
      const countEl = icon.querySelector('.cart-bubble__text-count');
      const n = countEl ? parseInt(countEl.textContent, 10) : NaN;
      if (Number.isFinite(n) && n > 0) paintCount(n, true);
    });
  };

  const arm = () => {
    installBagStyle();
    patchSparkOptionsSetter();
    resealHooks();
    wrapUpdateCart();
    revealExistingCount();
    if (window.spark) syncFromSpark();
    if (lastCount > 0) paintCount(lastCount, true);
  };

  document.addEventListener('fixatech:spark-cart', onSparkCart);
  document.addEventListener('fixatech:spark-cart-pending', startPoll);
  document.addEventListener('sparklayer:ready', arm);
  ['addToCart', 'cartUpdate', 'spark:cart', 'sparklayer:cart'].forEach((name) => {
    window.addEventListener(name, startPoll);
    document.addEventListener(name, startPoll);
  });
  window.addEventListener('view-update', (event) => {
    const view = event && event.detail ? String(event.detail.view || '') : '';
    if (view === 'cart' || view === 'mini-cart' || view === 'basket') startPoll();
  });
  document.addEventListener('cart:update', () => {
    if (lastCount < 0) return;
    if (!isSparkSession() && lastCount === 0) return;
    setTimeout(() => paintCount(lastCount, true), 0);
  });
  document.addEventListener(
    'click',
    (event) => {
      const target = event.target;
      if (!target || !target.closest) return;
      if (target.closest(ATC_SEL) || target.closest('spark-pdp, spark-product-card')) {
        startPoll();
        return;
      }
      if (target.closest('cart-icon, [data-testid="cart-drawer-trigger"], .header-actions__cart-icon')) {
        syncFromSpark();
      }
    },
    true
  );
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) syncFromSpark();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', arm);
  } else {
    arm();
  }

  [0, 200, 800, 2000].forEach((ms) => setTimeout(arm, ms));

  if (window.customElements && customElements.whenDefined) {
    ['cart-icon', 'spark-pdp', 'spark-product-card', 'spark-drawer', 'spark-cart'].forEach((name) => {
      customElements.whenDefined(name).then(arm).catch(() => {});
    });
  }
})();
